import React from 'react';
import { MODALIDAD, etiquetaDe } from '../compartidos/etiquetasEstado';

/**
 * GridMensual — calendario mensual estilo grid (lun-dom × 5-6 filas).
 *
 * Cada día muestra:
 *  - Número del día (gris si es de otro mes)
 *  - Hasta 3 mini-barras coloreadas por estado dominante de las citas
 *  - Badge "+N" si hay más citas
 *
 * Click en un día → llama onSeleccionar(fechaISO).
 */

const DOW_HEADERS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']; // Lun primero

const COLOR_ESTADO = {
  reservada: '#10b981',
  confirmada: '#10b981',
  realizada: '#003c66',
  no_asistio: '#f59e0b',
  cancelada_usuario: '#cbd5e1',
  cancelada_sistemas: '#cbd5e1',
};

export default function GridMensual({
  year, month,            // mes a pintar (1-12)
  citas,                  // array de citas (ya filtradas)
  diaSeleccionado,        // fecha ISO seleccionada
  onSeleccionar,
}) {
  const hoyISO = todayISO();

  // Agrupar citas por fecha
  const porFecha = new Map();
  for (const c of citas) {
    if (!porFecha.has(c.fecha)) porFecha.set(c.fecha, []);
    porFecha.get(c.fecha).push(c);
  }

  // Calcular el primer día del grid: primer lunes <= día 1 del mes
  const primero = new Date(Date.UTC(year, month - 1, 1));
  const primerDow = (primero.getUTCDay() + 6) % 7; // 0=lun, 6=dom
  const offset = primerDow; // días a retroceder
  const inicioGrid = new Date(primero);
  inicioGrid.setUTCDate(inicioGrid.getUTCDate() - offset);

  // 6 filas × 7 columnas = 42 celdas
  const celdas = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(inicioGrid);
    d.setUTCDate(d.getUTCDate() + i);
    const iso = d.toISOString().slice(0, 10);
    celdas.push({
      iso,
      dia: d.getUTCDate(),
      esOtroMes: d.getUTCMonth() !== month - 1,
      esHoy: iso === hoyISO,
      esSel: iso === diaSeleccionado,
      citas: porFecha.get(iso) || [],
    });
  }

  return (
    <div>
      {/* Cabecera días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW_HEADERS.map((d) => (
          <div
            key={d}
            className="text-[10px] uppercase tracking-wider text-slate-400 text-center py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid de celdas */}
      <div className="grid grid-cols-7 gap-1">
        {celdas.map((c) => (
          <CeldaDia key={c.iso} celda={c} onClick={() => onSeleccionar(c.iso)} />
        ))}
      </div>
    </div>
  );
}

function CeldaDia({ celda, onClick }) {
  const { dia, esOtroMes, esHoy, esSel, citas } = celda;
  const totalCitas = citas.length;

  // Resumen visual: hasta 3 barras (cada una representa una cita) + "+N"
  const visibles = citas.slice(0, 3);
  const overflow = totalCitas - visibles.length;

  const claseBase = 'aspect-square min-h-[70px] border rounded-md p-1.5 flex flex-col text-left transition-colors ';
  let claseEstado = '';
  if (esSel) claseEstado = 'border-primary bg-primary/5 ring-2 ring-primary/30';
  else if (esHoy) claseEstado = 'border-primary/40 bg-primary/5';
  else if (esOtroMes) claseEstado = 'border-slate-100 bg-slate-50/50 hover:bg-slate-100';
  else claseEstado = 'border-slate-200 bg-white hover:bg-slate-50';

  return (
    <button
      type="button"
      onClick={onClick}
      className={claseBase + claseEstado}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={
          'text-xs tabular-nums ' +
          (esOtroMes
            ? 'text-slate-300'
            : esHoy
              ? 'text-primary font-medium'
              : 'text-slate-700')
        }>
          {dia}
        </span>
        {totalCitas > 0 && (
          <span className="text-[9px] text-slate-400 tabular-nums">
            {totalCitas}
          </span>
        )}
      </div>

      {totalCitas > 0 && (
        <div className="flex-1 flex flex-col gap-0.5 justify-end">
          {visibles.map((c) => (
            <div
              key={c.id}
              className="h-1 rounded-full"
              style={{ backgroundColor: COLOR_ESTADO[c.estado] || '#cbd5e1' }}
              title={`${c.hora_inicio || etiquetaDe(MODALIDAD, c.modalidad).label} ${c.usuario.nombre_completo}`}
            />
          ))}
          {overflow > 0 && (
            <div className="text-[8px] text-slate-400 tabular-nums">
              +{overflow}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
